import { Controller, useForm } from 'react-hook-form';

import { z } from 'zod';
import { useDesignBoardCollaboratorsContext } from '@modules/design-board/component/manage/collaborator-overview/context/DesignBoardCollaboratorsContext';
import { useFetcher } from '@remix-run/react';
import { AllowedActions } from '@modules/design-board/actions/handler-mappings';
import * as FormStyle from '@modules/design-board/component/manage/share-design-baord/form/ShareDesignBoardForm.style';
import * as S from '../../InviteCollaboratorForm.style';
import {
    Flex,
    Grid,
    pxArrayToRem,
    useStyles,
    Button,
    useTheme,
} from '@archipro-design/aria';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import classNames from 'classnames';
import { fetcherIsDone } from '~/utils/fetcherHelper';

interface ShareDesignBoardFormProps {
    onCancel: () => void;
}

const ShareDesignBoardForm = ({ onCancel }: ShareDesignBoardFormProps) => {
    const boardContext = useDesignBoardCollaboratorsContext();
    const fetcher = useFetcher();
    const { css } = useStyles();

    const theme = useTheme();
    const buttonBorder = `1px solid ${theme.siteVariables.colors.charcoal[250]}`;
    const {
        control,
        formState: { errors },
        handleSubmit,
    } = useForm<FormShapeType>({
        resolver: zodResolver(FormShape),
        defaultValues: {
            boardId: boardContext.state.boardId.toString(),
            email: '',
            message: '',
        },
        mode: 'onBlur',
    });

    const [isLoading, setIsLoading] = useState(false);

    const submit = (data: FormShapeType) => {
        fetcher.submit(
            {
                email: typeof data.email === 'string' ? data.email : '',
                message: data.message || '',
                boardId: data.boardId,
                boardName: boardContext.state.boardName,
                redirect: 'true',
                _action: AllowedActions.enum.share_design_board,
            },
            {
                method: 'post',
                action: location.pathname,
            }
        );
        setIsLoading(true);
    };

    useEffect(() => {
        if (fetcherIsDone(fetcher)) {
            boardContext.dispatch({
                type: 'closeShareBoardModal',
            });
        }
    }, [boardContext, fetcher]);

    return (
        <fetcher.Form
            className={css(S.ShareForm)}
            onSubmit={handleSubmit(submit)}
        >
            <Flex column={true} className={css(S.FormContainer, S.FormFlex)}>
                <Controller
                    name={'email'}
                    control={control}
                    render={({ field }) => (
                        <FormRow
                            label={'Email address*'}
                            error={errors.email?.message as string}
                            field={
                                <Flex
                                    className={classNames(
                                        css(S.InputCol, S.EmailInput)
                                    )}
                                >
                                    <input
                                        {...field}
                                        placeholder={'Enter email address'}
                                        className={classNames(
                                            css(S.EmailStyles)
                                        )}
                                    />
                                </Flex>
                            }
                        />
                    )}
                />

                <Controller
                    name="message"
                    control={control}
                    render={({ field }) => (
                        <FormRow
                            label={'Message'}
                            error={errors.message?.message as string}
                            field={
                                <Flex className={css(S.InputCol)}>
                                    <textarea
                                        {...field}
                                        placeholder={'Enter description'}
                                        className={classNames(
                                            css(S.MessageStyles)
                                        )}
                                    />
                                </Flex>
                            }
                        />
                    )}
                />
                <div
                    className={css(S.BottomButtons, S.ShareModalBottomButtons)}
                >
                    <Button
                        className={css(S.CancelButton)}
                        variant="outlined"
                        color="dark"
                        type={'button'}
                        size={16}
                        variables={{
                            darkOutlinedButtonBorderNormal: buttonBorder,
                        }}
                        onClick={() => onCancel()}
                        disabled={isLoading}
                    >
                        CANCEL
                    </Button>

                    <Button
                        color="dark"
                        type={'submit'}
                        size={16}
                        loading={isLoading}
                        disabled={isLoading}
                        variables={{
                            darkButtonBorderNormal: buttonBorder,
                            darkButtonBorderHover: buttonBorder,
                            darkButtonBorderSelected: buttonBorder,
                        }}
                    >
                        SHARE
                    </Button>
                </div>
            </Flex>
        </fetcher.Form>
    );
};

const FormRow = ({
    label,
    field,
    error,
}: {
    label: string;
    field: ReactNode;
    error?: string;
}) => {
    const { css } = useStyles();

    return (
        <Grid columns={pxArrayToRem([216, 348])}>
            <Flex column={true} className={css(FormStyle.LabelWrapper)}>
                {label}
            </Flex>
            <Flex column={true} className={css(FormStyle.RowWrapper)}>
                {field}
                {error && (
                    <div className={css(FormStyle.ErrorText)}>{error}</div>
                )}
            </Flex>
        </Grid>
    );
};

type FormShapeType = z.infer<typeof FormShape>;
const FormShape = z.object({
    boardId: z.coerce.string(),
    redirect: z.boolean().optional(),
    email: z.union(
        [
            z
                .string()
                .trim()
                .min(1, 'Email address is required')
                .email({ message: 'Email address is invalid' }),
            z.array(z.string().trim().email()),
        ],
        { required_error: 'required({message: )' }
    ),
    message: z.string().optional(),
});

export { ShareDesignBoardForm };
